import {useMutation, useQuery, useSuspenseQuery} from '@tanstack/react-query';
import {
  DcdOwnershipComponent,
  DcdOwnershipComponentSummary,
  DcdOwnershipServiceReferenceDetail,
  Team,
  TeamEdit,
  TeamLink,
  TeamService,
  WorkstreamBasic,
} from 'client';
import {getService} from 'utilities';
import {queryClient} from 'views/QueryClientWrapper';

export interface TeamsTreeItemOld {
  teamName: string;
  teamId: string;
  slug: string;
  subTeams?: TeamsTreeItemOld[];
}

export const useTeamsTreeSuspenseOld = (): TeamsTreeItemOld[] => {
  const {data} = useSuspenseQuery({
    queryKey: ['teamTree', 'old'],
    queryFn: getService(TeamService).getTeamsApiV1TeamsGet,
  });

  return data;
};

export const useTeamsTreeSuspense = () => {
  const {data} = useSuspenseQuery({
    queryKey: ['teams', 'tree', 'all'],
    queryFn: getService(TeamService).getTreeAllApiV1TeamsTreeGet,
  });

  return data;
};

export const useTeamsTree = (enabled: boolean) =>
  useQuery({
    queryKey: ['teams', 'tree', 'all'],
    queryFn: getService(TeamService).getTreeAllApiV1TeamsTreeGet,
    enabled,
  });

export const useDcdOwnershipComponents = (query?: string) => {
  const normalizedQuery = query?.trim() || undefined;
  return useQuery<DcdOwnershipComponentSummary[]>({
    queryKey: ['teams', 'ownership', normalizedQuery],
    queryFn: () =>
      getService(TeamService).getDcdOwnershipComponentsApiV1TeamsOwnershipGet(normalizedQuery),
    staleTime: 5 * 60 * 1000,
  });
};

export const useDcdOwnershipComponent = (componentId?: number) =>
  useQuery<DcdOwnershipComponent>({
    queryKey: ['teams', 'ownership', componentId],
    queryFn: () =>
      getService(TeamService).getDcdOwnershipComponentApiV1TeamsOwnershipComponentIdGet(
        componentId!
      ),
    enabled: componentId !== undefined,
  });

export const useDcdOwnershipComponentReference = (componentId?: number, referenceId?: number) =>
  useQuery<DcdOwnershipServiceReferenceDetail>({
    queryKey: ['teams', 'ownership', componentId, 'references', referenceId],
    queryFn: () =>
      getService(
        TeamService
      ).getDcdOwnershipComponentReferenceApiV1TeamsOwnershipComponentIdReferencesReferenceIdGet(
        componentId!,
        referenceId!
      ),
    enabled: componentId !== undefined && referenceId !== undefined,
  });

export const useTeamsCountTree = () =>
  useQuery({
    queryKey: ['teams', 'tree', 'counts', 'all'],
    queryFn: getService(TeamService).getTreeCountsAllApiV1TeamsTreecountsGet,
  });

export const useTeamEdit = () => {
  const {mutateAsync: editTeam, isPending} = useMutation({
    mutationFn: ({teamId, data}: {teamId: string; data: TeamEdit; resetTree?: boolean}) =>
      getService(TeamService).editApiV1TeamSlugPut(teamId, data),
    onSettled: (response) => {
      if (!response) {
        return;
      }

      queryClient.invalidateQueries({queryKey: ['teamTree', 'old']});
      queryClient.invalidateQueries({queryKey: ['team', response]});
      queryClient.invalidateQueries({queryKey: ['team', 'logs', response]});
      queryClient.invalidateQueries({queryKey: ['teams', 'tree', 'all']});
      queryClient.invalidateQueries({queryKey: ['teams', 'tree', 'counts', 'all']});
      queryClient.invalidateQueries({
        queryKey: ['team', 'hierarchy', response],
      });
    },
  });

  return {editTeam, isPending};
};

export const useTeamLinksEdit = () => {
  const {mutateAsync: editTeamLinks, isPending} = useMutation({
    mutationFn: ({slug, links}: {slug: string; links: TeamLink[]}) =>
      getService(TeamService).editTeamLinksApiV1TeamsSlugLinksPut(slug, {links}),
    onSuccess: (_response, {slug}) => {
      queryClient.invalidateQueries({queryKey: ['team', slug], exact: false});
      queryClient.invalidateQueries({queryKey: ['team', 'logs', slug], exact: false});
    },
  });

  return {editTeamLinks, isPending};
};

export const useTeamResourcesEdit = () => {
  const {mutateAsync: editTeamResources, isPending} = useMutation({
    mutationFn: ({
      slug,
      slack_channels,
      emails,
      links,
    }: {
      slug: string;
      slack_channels: string[];
      emails: string[];
      links: TeamLink[];
    }) =>
      getService(TeamService).editTeamResourcesApiV1TeamsSlugResourcesPut(slug, {
        slack_channels,
        emails,
        links,
      }),
    onSuccess: (_response, {slug}) => {
      queryClient.invalidateQueries({queryKey: ['team', slug], exact: false});
      queryClient.invalidateQueries({queryKey: ['team', 'logs', slug], exact: false});
    },
  });

  return {editTeamResources, isPending};
};

export const useTeamDelete = () => {
  const {mutateAsync: deleteTeam, isPending} = useMutation({
    mutationFn: (slug: string) => getService(TeamService).archiveApiV1TeamSlugArchivePost(slug),
    onSettled: (response, _, slug) => {
      if (!response) {
        return;
      }

      queryClient.invalidateQueries({queryKey: ['teamTree', 'old']});
      queryClient.invalidateQueries({queryKey: ['teams', 'tree', 'all']});
      queryClient.invalidateQueries({queryKey: ['teams', 'tree', 'counts', 'all']});
      queryClient.invalidateQueries({queryKey: ['team', slug]});
      queryClient.invalidateQueries({
        queryKey: ['team', 'hierarchy', slug],
      });
    },
  });

  return {deleteTeam, isPending};
};

export const useTeamCreate = () => {
  const {mutateAsync: createTeam, isPending} = useMutation({
    mutationFn: ({data}: {data: TeamEdit}) => getService(TeamService).createApiV1TeamPost(data),
  });

  return {createTeam, isPending};
};

interface TeamCount {
  employees: number;
  teams: number;
  rootTeamName: string;
}

export const useTeamsCount = () => {
  const {data, isLoading} = useQuery({
    queryKey: ['teamCount'],
    queryFn: getService(TeamService).getCountApiV1CountGet,
  });

  return {isLoading, data};
};

export const useTeamsCountSuspense = (): TeamCount => {
  const {
    data: {employees, teams, rootTeamName},
  } = useSuspenseQuery({
    queryKey: ['teamCount'],
    queryFn: getService(TeamService).getCountApiV1CountGet,
  });

  return {employees, teams, rootTeamName};
};

export const useTeamSuspense = ({
  slug,
  includeSubteamMembers = false,
}: {
  slug: string;
  includeSubteamMembers?: boolean;
}) => {
  const {data} = useSuspenseQuery({
    queryKey: ['team', slug, includeSubteamMembers],
    queryFn: () =>
      slug
        ? getService(TeamService).getTeamBySlugApiV1TeamsSlugSlugGet(slug, includeSubteamMembers)
        : null,
  });

  return data;
};

export const useHierarchy = ({slug}: {slug?: string}) => {
  const {data} = useQuery({
    queryKey: ['team', 'hierarchy', slug],
    queryFn: () => getService(TeamService).getHierarchyApiV1TeamsSlugHierarchyGet(slug ?? ''),
    enabled: !!slug,
  });

  return data;
};

export const useHierarchySuspense = ({slug}: {slug: string}) => {
  const {data} = useSuspenseQuery({
    queryKey: ['team', 'hierarchy', slug],
    queryFn: () => getService(TeamService).getHierarchyApiV1TeamsSlugHierarchyGet(slug),
  });

  return data;
};

export const useTeam = ({slug}: {slug?: string}) => {
  const {data} = useQuery({
    queryKey: ['team', slug],
    queryFn: () => getService(TeamService).getTeamBySlugApiV1TeamsSlugSlugGet(slug ?? ''),
    enabled: !!slug,
  });

  return data;
};

export const useTeamMembers = ({slug}: {slug?: string}) => {
  const {data} = useQuery({
    queryKey: ['team', 'members', slug],
    queryFn: () => getService(TeamService).getTeamMembersApiV1TeamsSlugSlugMembersGet(slug ?? ''),
    enabled: !!slug,
  });

  return data;
};

export const useTeamWorkstreams = ({slug}: {slug?: string}) => {
  const {data = [], isLoading} = useQuery<WorkstreamBasic[]>({
    queryKey: ['team', 'workstreams', slug],
    queryFn: () =>
      getService(TeamService).getTeamWorkstreamsApiV1TeamsSlugSlugWorkstreamsGet(slug ?? ''),
    enabled: !!slug,
  });

  return {data, isLoading};
};

export const useTeamsMap = ({slugs}: {slugs: string[]}) => {
  const {
    data = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['teams', slugs],
    queryFn: () =>
      slugs.length
        ? getService(TeamService).getTeamBySlugsApiV1TeamsSlugsSlugsGet(slugs.join(','))
        : [],
    enabled: slugs.length > 0,
  });

  const teamsMap = data.reduce((acc, team) => {
    return {
      ...acc,
      [team.slug ?? '']: team,
    };
  }, {});

  return {teamsMap, isLoading, error};
};

export const useTeamsMapSuspense = ({slugs}: {slugs: string[]}) => {
  const {data} = useSuspenseQuery({
    queryKey: ['teams', slugs],
    queryFn: () =>
      slugs.length
        ? getService(TeamService).getTeamBySlugsApiV1TeamsSlugsSlugsGet(slugs.join(','))
        : [],
  });

  const teamsMap = data.reduce((acc, team) => {
    return {
      ...acc,
      [team.slug ?? '']: team,
    };
  }, {}) as Record<string, Team>;

  return teamsMap;
};
